import React, { useEffect, useRef } from 'react';
import { FlagIcon, MapPinIcon, PlusIcon, TruckTrailerIcon } from '@phosphor-icons/react';

import classNames from '../../../../utils/classnames';
import { formatDayMonth, formatWeekday } from '../../../../utils/date';
import { formatNumber } from '../../../../utils/number';
import { displayTrailer } from '../../../trailer/displayTrailer';
import type { WeeklyTrailer, WeeklyTrip, WeeklyTruck } from '../../models';
import { StatusBadge } from './TripStatus';
import { WeekProgress } from './WeekProgress';

export interface WeeklyCell {
  truckId: string;
  date: string;
}

const GRID_COLUMNS = 'grid grid-cols-[188px_repeat(6,minmax(0,1fr))_208px] divide-x divide-border';

function sumKm(trips: WeeklyTrip[]): number {
  return trips.reduce((sum, trip) => sum + trip.km, 0);
}

interface WeeklyBoardTableProps {
  days: string[];
  trucks: WeeklyTruck[];
  selected: WeeklyCell | null;
  onSelectCell: (cell: WeeklyCell) => void;
  onTargetChange: (truckId: string, targetKm: number) => Promise<void>;
}

/** Carriers down, Monday–Saturday across, a week total against target on the right. */
export const WeeklyBoardTable: React.FC<WeeklyBoardTableProps> = ({
  days,
  trucks,
  selected,
  onSelectCell,
  onTargetChange,
}) => {
  const dayTotals = days.map((date) =>
    trucks.reduce((sum, truck) => sum + sumKm(truck.trips.filter((t) => t.date === date)), 0),
  );
  const weekTotal = dayTotals.reduce((sum, km) => sum + km, 0);
  const targetTotal = trucks.reduce((sum, truck) => sum + (truck.targetKm ?? 0), 0);

  return (
    <div className="min-w-[1180px] overflow-hidden rounded-lg border border-border bg-surface">
      <div className={classNames(GRID_COLUMNS, 'h-11 border-b border-border')}>
        <span className="flex items-center px-4 text-xs font-medium text-fg-muted">Vervoerder</span>
        {days.map((date) => (
          <span key={date} className="flex items-center gap-1.5 px-3">
            <span className="text-xs font-semibold text-fg">{formatWeekday(date)}</span>
            <span className="text-2xs text-fg-muted">{formatDayMonth(date)}</span>
          </span>
        ))}
        <span className="flex items-center px-4 text-xs font-medium text-fg-muted">Week · km / target</span>
      </div>

      {trucks.map((truck) => (
        <div key={truck.id} className={classNames(GRID_COLUMNS, 'border-b border-border')}>
          <div className="flex min-w-0 flex-col gap-0.5 px-4 py-3.5">
            <span className="truncate text-sm font-semibold text-fg" title={truck.supplierName}>
              {truck.supplierName}
            </span>
            <span className="text-2xs text-fg-muted">{truck.licensePlate}</span>
          </div>
          {days.map((date) => (
            <DayCell
              key={date}
              trips={truck.trips.filter((t) => t.date === date)}
              trailer={truck.trailers.find((t) => t.date === date)?.trailer ?? null}
              isSelected={selected?.truckId === truck.id && selected.date === date}
              label={`${truck.supplierName} ${truck.licensePlate}, ${formatWeekday(date)} ${formatDayMonth(date)}`}
              onSelect={() => onSelectCell({ truckId: truck.id, date })}
            />
          ))}
          <div className="px-4 py-3.5">
            <WeekProgress
              km={sumKm(truck.trips)}
              targetKm={truck.targetKm}
              onTargetChange={(targetKm) => onTargetChange(truck.id, targetKm)}
            />
          </div>
        </div>
      ))}

      <div className={classNames(GRID_COLUMNS, 'bg-surface-sunken')}>
        <div className="flex flex-col gap-0.5 px-4 py-3.5">
          <span className="text-sm font-semibold text-fg">Totaal</span>
          <span className="text-2xs text-fg-muted">
            {trucks.length} {trucks.length === 1 ? 'vervoerder' : 'vervoerders'}
          </span>
        </div>
        {dayTotals.map((km, idx) => (
          <span key={days[idx]} className="px-3 py-3.5 text-xs font-medium text-fg-secondary">
            {formatNumber(km, 0)} km
          </span>
        ))}
        <div className="px-4 py-3.5">
          <WeekProgress km={weekTotal} targetKm={targetTotal || null} />
        </div>
      </div>
    </div>
  );
};

interface DayCellProps {
  trips: WeeklyTrip[];
  trailer: WeeklyTrailer | null;
  isSelected: boolean;
  label: string;
  onSelect: () => void;
}

const DayCell: React.FC<DayCellProps> = ({ trips, trailer, isSelected, label, onSelect }) => {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the selected cell clear of the day panel that opens over the right side.
  useEffect(() => {
    if (isSelected) ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [isSelected]);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-pressed={isSelected}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={classNames(
        'group flex min-h-24 min-w-0 cursor-pointer flex-col gap-3 px-3 py-3.5 outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
        isSelected ? 'bg-brand-subtle ring-[1.5px] ring-inset ring-brand' : 'hover:bg-surface-hover',
      )}
    >
      {trailer && (
        <TripLine icon={<TruckTrailerIcon className="size-[11px] text-fg-muted" />} text={displayTrailer(trailer)} />
      )}
      {trips.length === 0 && !trailer ? (
        <span
          className={classNames(
            'flex flex-1 items-center justify-center gap-1.5 text-2xs font-medium text-fg-secondary transition-opacity',
            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
          )}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Rit toevoegen
        </span>
      ) : (
        trips.map((trip) => <TripSummary key={trip.id} trip={trip} />)
      )}
    </div>
  );
};

const TripSummary: React.FC<{ trip: WeeklyTrip }> = ({ trip }) => (
  <div className="flex flex-col gap-[3px]">
    {trip.loading && (
      <TripLine
        icon={
          <StatusBadge status={trip.loadingStatus}>
            <MapPinIcon weight="fill" />
          </StatusBadge>
        }
        text={trip.loading}
        isMajorIssue={trip.loadingStatus === 'MajorIssue'}
      />
    )}
    {trip.unloading && (
      <TripLine
        icon={
          <StatusBadge status={trip.unloadingStatus}>
            <FlagIcon weight="fill" />
          </StatusBadge>
        }
        text={trip.unloading}
        isMajorIssue={trip.unloadingStatus === 'MajorIssue'}
      />
    )}
    <span className="pl-[22px] pt-0.5 text-2xs text-fg-muted">{formatNumber(trip.km, 0)} km</span>
  </div>
);

interface TripLineProps {
  icon: React.ReactElement;
  text: string;
  isMajorIssue?: boolean;
}

const TripLine: React.FC<TripLineProps> = ({ icon, text, isMajorIssue = false }) => (
  <div className="flex gap-1.5 text-xs">
    {/* One line tall, so the icon centres on the first line of text whatever its line height. */}
    <span className="flex h-lh w-4 shrink-0 items-center justify-center">{icon}</span>
    <span
      className={classNames(
        'min-w-0 whitespace-pre-line wrap-break-word',
        isMajorIssue ? 'font-medium text-negative-text' : 'text-fg',
      )}
    >
      {text}
    </span>
  </div>
);
